import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { News } from 'src/shared/models/notifications/news.model';
import { Reply } from 'src/shared/models/notifications/reply.model';
import { Booking } from 'src/shared/models/booking.model';
import { NewsProvider } from './news.provider';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { BookingProvider } from './booking.provider';
import { RequestProvider } from './request.provider';
import { LocalStorageService } from './local-storage.service';
import { AuthenticatedHttpService } from './authenticatedHttpService.service';
import * as fp from "lodash/fp";

export enum SyncWrapResult {
	Error, SyncLater, Success, SuccessAndSync
}

@Injectable({ providedIn: 'root' })
export class SyncService {
	static readonly RETRY_PENDING_REPLIES = "RETRY_PENDING_REPLIES";
	static readonly RETRY_PENDING_REQUESTS = "RETRY_PENDING_REQUESTS";
	static readonly RETRY_SET_READ_STATUS = "RETRY_SET_READ_STATUS";
	static readonly STORAGE_OFFLINE_BOOKINGS = "STORAGE_OFFLINE_BOOKINGS";

	static GetKey(key: string, userId: string) {
		return `${key}_${userId}`;
	}

	private readonly _bookingsSynced = signal<boolean>(false);
	public readonly bookingsSynced = this._bookingsSynced.asReadonly();
	public readonly bookingsSynced$ = toObservable(this._bookingsSynced);

	private _pendingNews = signal<Array<News>>([]);
	public readonly pendingNews$ = toObservable(this._pendingNews);

	// Simplifying wrap calls.
	// Either All or None messageKeys are required.
	syncMap = {
		[SyncService.RETRY_PENDING_REPLIES]: {
			service: this.newsService,
			request: this.newsService.reply,
			syncMethod: this.syncReplies,
			successMessageKey: "Reply_sent",
			successAndSyncMessageKey: "Replies_sent_and_synced",
			syncLaterHeaderMessageKey: "No_Connection_try_later_Header",
			syncLaterMessageKey: "No_Connection_try_later",
			equals: Reply.equals,
		},
		[SyncService.RETRY_PENDING_REQUESTS]: {
			service: this.requestsService,
			request: this.requestsService.createApplication,
			syncMethod: this.syncRequests,
			successMessageKey: "NewsRequest_sent",
			successAndSyncMessageKey: "NewsRequest_sent_and_synced",
			syncLaterHeaderMessageKey: "No_Connection_try_later_Header",
			syncLaterMessageKey: "No_Connection_try_later",
		},
		[SyncService.RETRY_SET_READ_STATUS]: {
			service: this.newsService,
			request: this.newsService.read,
			syncMethod: this.syncReadStatus,
			syncLaterHeaderMessageKey: "No_Connection_try_later_Header",
			syncLaterMessageKey: "No_Connection_try_later",
		},
		[SyncService.STORAGE_OFFLINE_BOOKINGS]: {
			service: this.bookingProvider,
			request: this.bookingProvider.bookData,
			syncMethod: this.syncBookings,
			successMessageKey: "Booking_sent",
			successAndSyncMessageKey: "Booking_sent_and_synced",
			syncLaterHeaderMessageKey: "No_Connection_try_later_Header",
			syncLaterMessageKey: "No_Connection_try_later",
			equals: Booking.equals,
		},
	}

	constructor(
		private storage: LocalStorageService,
		// private alertController: AlertController,
		private translateService: TranslateService,
		private newsService: NewsProvider,
		private bookingProvider: BookingProvider,
		private requestsService: RequestProvider,
	) {
		// Initialize pending news after observable is created
		this.initPendingNews();
	}

	async wrapRequest<T, R>(mapKey: string, data: T): Promise<{ status: SyncWrapResult, result: R | undefined, error: any, message: string, headerMessage: string }> {
		const meta = this.syncMap[mapKey as keyof typeof this.syncMap];
		if (!meta) throw new Error("Invalid key");

		let [status, result, error] = [SyncWrapResult.Error, , ,];

		try {
			const boundRequest = meta.request.bind(meta.service);

			result = await HttpUtils.handle<R>({ request: boundRequest(data as any) }) as any;

			const edgeCase = await this.unpend(data, mapKey, fp.get("equals", meta));
			if (edgeCase) { // Handling Edge case with queue emptied by unpend. Showing proper notif
				status = SyncWrapResult.SuccessAndSync;
			} else {
				const anySynced = await meta.syncMethod.apply(this);
				status = anySynced ? SyncWrapResult.SuccessAndSync : SyncWrapResult.Success;
			}
		} catch (err) {
			error = err as any
			if (HttpUtils.isTimeout(error)
				|| HttpUtils.isServerError(error)
				|| HttpUtils.isForbidden(error)
				|| HttpUtils.isNotAuthenticatedError(error)) {
				await this.pend(data, mapKey, fp.get("equals", meta));
				status = SyncWrapResult.SyncLater;
			}
		}

		this.sideEffects(mapKey);

		const message = await this.describe(status, meta, error);
		return {
			status, result, error,
			...message
		};
	}
	private async describe(status: SyncWrapResult, meta: any, error: any): Promise<{ message: string, headerMessage: string }> {
		let message: string | undefined = '';
		let headerMessage: string | undefined = '';

		if (meta.successMessageKey) {
			const messageOrObs = this.translateService.get(meta.successMessageKey);
			message = await this.resolveToString(messageOrObs);
		}

		if (meta.successAndSyncMessageKey && status === SyncWrapResult.SuccessAndSync) {
			const messageOrObs = this.translateService.get(meta.successAndSyncMessageKey);
			message = await this.resolveToString(messageOrObs);
		}

		if (meta.syncLaterMessageKey && status === SyncWrapResult.SyncLater) {
			const messageOrObs = this.translateService.get(meta.syncLaterMessageKey);
			message = await this.resolveToString(messageOrObs);

			if (meta.syncLaterHeaderMessageKey) {
				const headerOrObs = this.translateService.get(meta.syncLaterHeaderMessageKey);
				headerMessage = await this.resolveToString(headerOrObs);
			}
		}

		if (status === SyncWrapResult.Error) {
			message = HttpUtils.stringifyError(error);
		}

		return { message: message ?? '', headerMessage: headerMessage ?? '' };
	}
	private async resolveToString(value: string | Observable<string> | undefined): Promise<string | undefined> {
		if (value instanceof Observable) {
			return await value.toPromise();
		}
		return value ?? '';
	}

	private async sideEffects(mapKey: string) {
		try {
			if (mapKey === SyncService.RETRY_PENDING_REPLIES)
				this.initPendingNews();
		} catch (error) {
			console.error(error);
		}
	}

	async pend<T>(target: T | T[], storeKey: string, customEquals?: (a: T, b: T) => boolean) {
		if (!target || !storeKey) throw new Error("Invalid input");

		const alreadyPending: T[] = await this.getFromStorage(storeKey) || [];

		let items: T[] = Array.isArray(target) ? target : [target];

		if (alreadyPending.length) {
			// Ensure the customEquals function has the correct type
			items = fp.uniqWith(customEquals || fp.isEqual, [...alreadyPending, ...items]) as T[];
		}

		const keyWithUserId = await this.getStoreKey(storeKey);
		await this.storage.set(keyWithUserId, items);
	}
	async unpend<T>(target: T | Array<T>, storeKey: string, customEquals?: Function) {
		if (!target || !storeKey) throw new Error("Invalid input");

		let alreadyPending = await this.getFromStorage(storeKey) || [];
		const queNotEmpty = fp.get("length", alreadyPending);

		let items = target instanceof Array ? target : [target];
		if (alreadyPending && alreadyPending.length) {
			alreadyPending = alreadyPending.filter((ap: any) => !items.some(item => (customEquals || fp.isEqual)(ap, item)));
		}
		const queEmpty = !fp.get("length", alreadyPending);

		const keyWithUserId = await this.getStoreKey(storeKey);
		await this.storage.set(keyWithUserId, alreadyPending);

		return queNotEmpty && queEmpty;
	}

	[syncAllKey: string]: any;
	async syncAll(alert = false): Promise<boolean> {
		const pendingKey = `${this.syncAll.name}Pending`;
		if (this[pendingKey]) {
			return false;
		}
		this[pendingKey] = true;

		await this.renameOldCachedKeys();

		let somethingSynced = false;

		const syncs: Array<Promise<boolean>> = [];

		try { // those sync method must not throw
			syncs.push(this.syncReplies());
			syncs.push(this.syncRequests());
			syncs.push(this.syncReadStatus());
			syncs.push(this.syncBookings());
			somethingSynced = (await Promise.all(syncs)).reduce((a, v) => a || v);
		} catch (error) {
			console.error(error);
		}

		if (alert && somethingSynced) {
			// const message = await this.translateService.get("Synchronized_Alert").toPromise();
			// this.alertController
			// 	.create({
			// 		message: message,
			// 		cssClass: 'custom-alert',
			// 		buttons: [this.translator.dictionary.General_Okay]
			// 	})
			// 	.present();
		}

		delete this[`${this.syncAll.name}Pending`];
		return somethingSynced
	}
	async syncReplies() {
		const meta = this.syncMap[SyncService.RETRY_PENDING_REPLIES];
		const anySynced = await this.syncItems<Reply>(meta.service, meta.request, SyncService.RETRY_PENDING_REPLIES);
		if (anySynced)
			this.initPendingNews();
		return anySynced;
	}
	async syncRequests() {
		const meta = this.syncMap[SyncService.RETRY_PENDING_REQUESTS];
		const anySynced = await this.syncItems<Reply>(meta.service, meta.request, SyncService.RETRY_PENDING_REQUESTS);
		if (anySynced)
			this.initPendingNews();
		return anySynced;
	}
	async syncReadStatus() {
		const meta = this.syncMap[SyncService.RETRY_SET_READ_STATUS];
		return await this.syncItems(meta.service, meta.request, SyncService.RETRY_SET_READ_STATUS);
	}
	async syncBookings() {
		const meta = this.syncMap[SyncService.STORAGE_OFFLINE_BOOKINGS];
		const isSynced = await this.syncItems<Booking>(meta.service, meta.request, SyncService.STORAGE_OFFLINE_BOOKINGS);
		this._bookingsSynced.set(isSynced);
		return isSynced;
	}
	// Returns:
	// false if nothing synced
	// undefined on error
	// true in something has been synced
	private async syncItems<T>(context: any, sync: Function, storeKey: string, extraParams = []): Promise<boolean> {
		const pendingKey = `${this.syncItems.name}${storeKey}Pending`;
		if (this[pendingKey]) {
			return false;
		}
		this[pendingKey] = true;

		try {
			const retryLater: any[] = [];
			let failedCount = 0;

			const items = await this.getFromStorage(storeKey);
			if (!items || !items.length) {
				return false;
			}

			if (storeKey === SyncService.STORAGE_OFFLINE_BOOKINGS || storeKey === 'STORAGE_NOT_SYNC_BOOKINGS') {
				items.forEach((item: any) => item.isOfflineBooking = true);
			}

			await Promise.all(items.map(async (item: any) => {
				try {
					await sync.apply(context, [item, ...extraParams]);
				}
				catch (error) {
					if (HttpUtils.isTimeout(error)
						|| HttpUtils.isForbidden(error)
						|| HttpUtils.isNotAuthenticatedError(error)) {
						retryLater.push(item);
					}
					++failedCount;
				}
			}));

			const keyWithUserId = await this.getStoreKey(storeKey);
			await this.storage.set(keyWithUserId, retryLater);

			return items.length > failedCount;
		}
		catch (error) {
			console.error(error);
			return false;
		}
		finally {
			delete this[`${this.syncItems.name}${storeKey}Pending`];
		}
	}

	private async renameOldCachedKeys() {
		// Note: new improvement - store cache by userId. 
		// So we need to rename all old cached keys.

		// rename replies
		const replies = await this.storage.get(SyncService.RETRY_PENDING_REPLIES);
		const repliesNotEmpty = fp.get("length", replies);
		if (repliesNotEmpty) {
			const repliesKeyWithUserId = await this.getStoreKey(SyncService.RETRY_PENDING_REPLIES);
			await this.storage.set(repliesKeyWithUserId, replies);
			await this.storage.remove(SyncService.RETRY_PENDING_REPLIES);
			console.log(`storage key \"${SyncService.RETRY_PENDING_REPLIES}\" was renamed into \"${repliesKeyWithUserId}\".`);
		}

		// rename requests
		const requests = await this.storage.get(SyncService.RETRY_PENDING_REQUESTS);
		const requestsNotEmpty = fp.get("length", requests);
		if (requestsNotEmpty) {
			const requestsKeyWithUserId = await this.getStoreKey(SyncService.RETRY_PENDING_REQUESTS);
			await this.storage.set(requestsKeyWithUserId, requests);
			await this.storage.remove(SyncService.RETRY_PENDING_REQUESTS);
			console.log(`storage key \"${SyncService.RETRY_PENDING_REQUESTS}\" was renamed into \"${requestsKeyWithUserId}\".`);
		}

		// rename readStatus
		const readStatus = await this.storage.get(SyncService.RETRY_SET_READ_STATUS);
		const readStatusNotEmpty = !!readStatus;
		if (readStatusNotEmpty) {
			const readStatusKeyWithUserId = await this.getStoreKey(SyncService.RETRY_SET_READ_STATUS);
			await this.storage.set(readStatusKeyWithUserId, readStatus);
			await this.storage.remove(SyncService.RETRY_SET_READ_STATUS);
			console.log(`storage key \"${SyncService.RETRY_SET_READ_STATUS}\" was renamed into \"${readStatusKeyWithUserId}\".`);
		}

		// rename bookins
		const bookins = await this.storage.get(SyncService.STORAGE_OFFLINE_BOOKINGS);
		const bookinsNotEmpty = fp.get("length", bookins);
		if (bookinsNotEmpty) {
			const bookingsKeyWithUserId = await this.getStoreKey(SyncService.STORAGE_OFFLINE_BOOKINGS);
			await this.storage.set(bookingsKeyWithUserId, bookins);
			await this.storage.remove(SyncService.STORAGE_OFFLINE_BOOKINGS);
			console.log(`storage key \"${SyncService.STORAGE_OFFLINE_BOOKINGS}\" was renamed into \"${bookingsKeyWithUserId}\".`);
		}
	}

	private async getStoreKey(storeKey: string) {
		const userId = await this.getUserId();
		return `${storeKey}_${userId}`;
	}

	private async getFromStorage(key: string) {
		const keyWithUserId = await this.getStoreKey(key);
		const storageDataWithUserId = await this.storage.get(keyWithUserId);
		return storageDataWithUserId;
	}

	private async getUserId(): Promise<string> {
		const authData = await this.storage.get(AuthenticatedHttpService.AUTHENTICATION_DATA);
		if (!authData || !authData.jwt) return '';
		return authData.jwt.sub;
	}

	private async initPendingNews() {
		const [replies] = await Promise.all([
			this.getFromStorage(SyncService.RETRY_PENDING_REPLIES),
			// this.storage.get(SyncService.RETRY_PENDING_REQUESTS)
		]);
		this._pendingNews.set([
			...(replies || [])
		]);
	}
}